import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { LoadingSpinner } from "../ui/loading-spinner";
import { useToast } from "../../hooks/use-toast";
import { format } from 'date-fns';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../../firebase';
import { AlertCircle, Bell, Calendar as CalendarIcon, Check, Edit, Eye, Trash2, XCircle } from 'lucide-react'; // Renamed Calendar import
import { Badge } from "../../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover"; // Ensure Popover components are imported
import { cn } from "../../lib/utils"; // Import cn utility
import { SimpleCalendar } from "../ui/simple-calendar"; // already imported

const NotificationManager = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    level: 'info',
    global: true,
    expiresAt: null,
    targetUsers: [],
    isActive: true
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Add separate state variables for Add and Edit dialogs
  const [addDatePopoverOpen, setAddDatePopoverOpen] = useState(false);
  const [editDatePopoverOpen, setEditDatePopoverOpen] = useState(false);

  const { toast } = useToast();

  // Load notifications on component mount
  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const notificationsRef = collection(db, 'notifications');
      const q = query(notificationsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const notificationsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to JS Date objects for easier handling
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        expiresAt: doc.data().expiresAt?.toDate() || null,
      }));

      setNotifications(notificationsData);
    } catch (error) {
      console.error("Error loading notifications:", error);
      toast({
        title: "Error",
        description: "Failed to load notifications",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddNotification = () => {
    // Reset form data to defaults
    setFormData({
      title: '',
      message: '',
      level: 'info',
      global: true,
      expiresAt: null,
      targetUsers: [],
      isActive: true
    });
    setShowAddDialog(true);
  };

  const handleEditNotification = (notification) => {
    setSelectedNotification(notification);
    setFormData({
      title: notification.title || '',
      message: notification.message || '',
      level: notification.level || 'info',
      global: notification.global ?? true,
      expiresAt: notification.expiresAt,
      targetUsers: notification.targetUsers || [],
      isActive: notification.isActive ?? true
    });
    setShowEditDialog(true);
  };

  const handleDeleteNotification = (notification) => {
    setSelectedNotification(notification);
    setShowDeleteDialog(true);
  };

  const handleSubmitAdd = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Ensure expiresAt is a Date or null
      let expiresAtValue = formData.expiresAt;
      if (expiresAtValue && typeof expiresAtValue === "string") {
        expiresAtValue = new Date(expiresAtValue);
      }
      // Log for debugging
      console.log('Saving notification, expiresAt:', expiresAtValue);

      await addDoc(collection(db, 'notifications'), {
        ...formData,
        expiresAt: expiresAtValue ? Timestamp.fromDate(expiresAtValue) : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      toast({
        title: "Success",
        description: "Notification created successfully",
        variant: "success"
      });

      setShowAddDialog(false);
      loadNotifications();
    } catch (error) {
      console.error("Error creating notification:", error);
      toast({
        title: "Error",
        description: "Failed to create notification",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitEdit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let expiresAtValue = formData.expiresAt;
      if (expiresAtValue && typeof expiresAtValue === "string") {
        expiresAtValue = new Date(expiresAtValue);
      }
      // Log for debugging
      console.log('Updating notification, expiresAt:', expiresAtValue);

      const notificationRef = doc(db, 'notifications', selectedNotification.id);
      await updateDoc(notificationRef, {
        ...formData,
        expiresAt: expiresAtValue ? Timestamp.fromDate(expiresAtValue) : null,
        updatedAt: serverTimestamp()
      });

      toast({
        title: "Success",
        description: "Notification updated successfully",
        variant: "success"
      });

      setShowEditDialog(false);
      loadNotifications();
    } catch (error) {
      console.error("Error updating notification:", error);
      toast({
        title: "Error",
        description: "Failed to update notification",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    setIsSubmitting(true);

    try {
      // Delete notification doc
      await deleteDoc(doc(db, 'notifications', selectedNotification.id));

      toast({
        title: "Success",
        description: "Notification deleted successfully",
        variant: "success"
      });

      setShowDeleteDialog(false);
      loadNotifications();
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast({
        title: "Error",
        description: "Failed to delete notification",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle changes to form data
  const handleChange = (field, value) => {
    // Log the raw input for debugging
    console.log(`handleChange called: field='${field}', value=`, value, `(type: ${typeof value})`);

    if (field === 'expiresAt') {
      let dateValue = value;
      console.log(`handleChange (expiresAt): Initial value received:`, dateValue); // Log initial value

      // Check if it's already a Date object
      if (dateValue instanceof Date) {
        console.log(`handleChange (expiresAt): Value is already a Date object.`);
      }
      // Check if it's a Firestore Timestamp (less likely from calendar, but good check)
      else if (dateValue && typeof dateValue.toDate === 'function') {
         console.log(`handleChange (expiresAt): Value is a Firestore Timestamp, converting to Date.`);
         dateValue = dateValue.toDate();
      }
      // Check if it's a string or number that needs conversion
      else if (dateValue && (typeof dateValue === 'string' || typeof dateValue === 'number')) {
        console.log(`handleChange (expiresAt): Value is string or number, attempting to convert to Date.`);
        dateValue = new Date(dateValue);
        // Check if the conversion resulted in a valid date
        if (isNaN(dateValue.getTime())) {
          console.warn(`handleChange (expiresAt): Conversion resulted in Invalid Date. Setting to null.`);
          dateValue = null;
        } else {
           console.log(`handleChange (expiresAt): Conversion successful:`, dateValue);
        }
      }
      // Handle cases where the value is null, undefined, or an unexpected type
      else if (dateValue == null) {
         console.log(`handleChange (expiresAt): Value is null or undefined. Setting to null.`);
         dateValue = null;
      }
      else {
        console.warn(`handleChange (expiresAt): Unexpected value type received: ${typeof dateValue}. Setting to null.`);
        dateValue = null;
      }

      console.log(`handleChange (expiresAt): Final dateValue before setting state:`, dateValue); // Log final value
      setFormData(prevData => ({
        ...prevData,
        [field]: dateValue // Use the processed dateValue
      }));
    } else {
      // Handle other fields normally
      setFormData(prevData => ({
        ...prevData,
        [field]: value
      }));
    }
  };

  // Get appropriate status color based on notification level
  const getStatusColor = (level) => {
    switch (level) {
      case 'critical':
        return 'bg-red-500/30 border-red-500/50 text-red-300';
      case 'warning':
        return 'bg-amber-500/30 border-amber-500/50 text-amber-300';
      case 'success':
        return 'bg-green-500/30 border-green-500/50 text-green-300';
      case 'info':
      default:
        return 'bg-blue-500/30 border-blue-500/50 text-blue-300';
    }
  };

  // Get appropriate icon based on notification level
  const getLevelIcon = (level) => {
    switch (level) {
      case 'critical':
        return <AlertCircle className="h-4 w-4 text-red-400" />;
      case 'warning':
        return <AlertCircle className="h-4 w-4 text-amber-400" />;
      case 'success':
        return <Check className="h-4 w-4 text-green-400" />;
      case 'info':
      default:
        return <Bell className="h-4 w-4 text-white" />; // Changed to white
    }
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'No expiration';
    // Ensure date is a JS Date object before formatting
    const dateObj = date instanceof Date ? date : (date?.toDate ? date.toDate() : null);
    if (!dateObj || isNaN(dateObj.getTime())) {
        console.warn("Invalid date passed to formatDate:", date);
        return 'Invalid Date';
    }
    return format(dateObj, 'MMM d, yyyy');
  };

  // Format date for display in the button trigger
  const formatDateForButton = (date) => {
    if (!date) return "Select date";
    // Ensure date is a JS Date object before formatting
    const dateObj = date instanceof Date ? date : (date?.toDate ? date.toDate() : null);
     if (!dateObj || isNaN(dateObj.getTime())) {
        console.warn("Invalid date passed to formatDateForButton:", date);
        return 'Invalid Date';
    }
    return format(dateObj, 'PPP'); // e.g., Jun 1, 2023
  };


  return (
    <div className="container mx-auto p-4 max-w-6xl">
      <Card className="bg-[#2A2A3E] border-purple-500/20 shadow-lg">
        <CardHeader className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle className="text-white text-2xl">System Notifications</CardTitle>
            <CardDescription className="text-gray-300">
              Manage notifications that appear on user dashboards
            </CardDescription>
          </div>
          <Button 
            className="bg-purple-600 hover:bg-purple-700 text-white mt-4 md:mt-0"
            onClick={handleAddNotification}
          >
            <Bell className="h-4 w-4 mr-2" />
            Create Notification
          </Button>
        </CardHeader>

        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="large" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="bg-[#1F1F2E] rounded-lg p-8 text-center">
              <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg text-gray-300 font-medium">No Notifications</h3>
              <p className="text-gray-400 mt-2">
                Create notifications to inform users about maintenance, updates, or other important information.
              </p>
              <Button 
                className="bg-purple-600 hover:bg-purple-700 text-white mt-4"
                onClick={handleAddNotification}
              >
                Create First Notification
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`bg-[#1F1F2E] rounded-lg border ${notification.isActive ? 'border-purple-500/20' : 'border-gray-700'} p-4`}
                >
                  <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-3">
                    <div className="flex items-center mb-2 sm:mb-0">
                      {getLevelIcon(notification.level)}
                      <h3 className={`text-lg font-medium ml-2 ${notification.level === 'info' ? 'text-white' : notification.isActive ? 'text-white' : 'text-gray-400'}`}>
                        {notification.title}
                      </h3>

                      {/* Status badge */}
                      <Badge className={`ml-3 ${getStatusColor(notification.level)}`}>
                        {notification.level.charAt(0).toUpperCase() + notification.level.slice(1)}
                      </Badge>
                    </div>

                    {/* Action buttons */}
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditNotification(notification)}
                        className="border-purple-500/20 text-purple-300 hover:bg-purple-500/10"
                      >
                        <Edit className="h-4 w-4" />
                        <span className="sr-only md:not-sr-only md:ml-2">Edit</span>
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteNotification(notification)}
                        className="border-red-500/20 text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only md:not-sr-only md:ml-2">Delete</span>
                      </Button>
                    </div>
                  </div>

                  {/* Message content */}
                  <div className={`text-sm ${notification.isActive ? 'text-gray-300' : 'text-gray-400'} mb-3`}>
                    {notification.message}
                  </div>

                  {/* Metadata */}
                  <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-400">
                    <div className="flex items-center">
                      <CalendarIcon className="h-3.5 w-3.5 mr-1.5" /> {/* Use CalendarIcon */}
                      Created: {formatDate(notification.createdAt)} {/* Use formatDate */}
                    </div>

                    <div className="flex items-center">
                      <CalendarIcon className="h-3.5 w-3.5 mr-1.5" /> {/* Use CalendarIcon */}
                      Expires: {notification.expiresAt ? formatDate(notification.expiresAt) : 'No expiration'} {/* Use formatDate */}
                    </div>

                    {notification.global && (
                      <div className="flex items-center">
                        <Eye className="h-3.5 w-3.5 mr-1.5" />
                        All users
                      </div>
                    )}

                    <div className="flex items-center">
                      <span className={`w-2 h-2 rounded-full mr-1.5 ${notification.isActive ? 'bg-green-500' : 'bg-gray-500'}`}></span>
                      {notification.isActive ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Notification Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-[#2A2A3E] text-white border-purple-500/20">
          <DialogHeader>
            <DialogTitle className="text-xl">Create New Notification</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitAdd}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="title" className="text-sm text-gray-300">Title</Label>
                <Input 
                  id="title"
                  placeholder="Notification title"
                  className="bg-[#1F1F2E] border-purple-500/30 text-white mt-1"
                  required
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="message" className="text-sm text-gray-300">Message</Label>
                <Textarea 
                  id="message"
                  placeholder="Notification message"
                  className="bg-[#1F1F2E] border-purple-500/30 text-white mt-1"
                  required
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="level" className="text-sm text-gray-300">Level</Label>
                  <Select 
                    value={formData.level} 
                    onValueChange={(value) => handleChange('level', value)}
                  >
                    <SelectTrigger id="level" className="bg-[#1F1F2E] border-purple-500/30 text-white mt-1">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2A2A3E] border-purple-500/30">
                      <SelectItem value="info" className="text-white">Info</SelectItem>
                      <SelectItem value="success" className="text-white">Success</SelectItem>
                      <SelectItem value="warning" className="text-white">Warning</SelectItem>
                      <SelectItem value="critical" className="text-white">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-gray-300">Expiration Date</Label>
                  <div className="mt-1">
                    <Popover open={addDatePopoverOpen} onOpenChange={setAddDatePopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal bg-[#1F1F2E] border-purple-500/30 text-white",
                            !formData.expiresAt && "text-gray-400"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formatDateForButton(formData.expiresAt)}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#1F1F2E] border-purple-500/30">
                        <SimpleCalendar
                          value={formData.expiresAt}
                          onChange={(date) => {
                            console.log('SimpleCalendar (Add Dialog) onChange triggered with date:', date);
                            handleChange('expiresAt', date);
                            setAddDatePopoverOpen(false);
                          }}
                          minDate={new Date()}
                          className="rounded-md border-purple-500/30"
                        />
                      </PopoverContent>
                    </Popover>
                    {/* Keep Clear Date button */}
                    {formData.expiresAt && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleChange('expiresAt', null)}
                        className="text-xs text-gray-400 mt-2"
                      >
                        Clear Date
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="global"
                  checked={formData.global}
                  onCheckedChange={(checked) => handleChange('global', checked)}
                />
                <Label htmlFor="global" className="text-sm text-gray-300">Show to all users</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => handleChange('isActive', checked)}
                />
                <Label htmlFor="active" className="text-sm text-gray-300">Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddDialog(false)}
                className="bg-transparent border-gray-600 text-gray-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Notification'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Notification Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="bg-[#2A2A3E] text-white border-purple-500/20">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit Notification</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitEdit}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-title" className="text-sm text-gray-300">Title</Label>
                <Input 
                  id="edit-title"
                  placeholder="Notification title"
                  className="bg-[#1F1F2E] border-purple-500/30 text-white mt-1"
                  required
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="edit-message" className="text-sm text-gray-300">Message</Label>
                <Textarea 
                  id="edit-message"
                  placeholder="Notification message"
                  className="bg-[#1F1F2E] border-purple-500/30 text-white mt-1"
                  required
                  value={formData.message}
                  onChange={(e) => handleChange('message', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-level" className="text-sm text-gray-300">Level</Label>
                  <Select 
                    value={formData.level} 
                    onValueChange={(value) => handleChange('level', value)}
                  >
                    <SelectTrigger id="edit-level" className="bg-[#1F1F2E] border-purple-500/30 text-white mt-1">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2A2A3E] border-purple-500/30">
                      <SelectItem value="info" className="text-white">Info</SelectItem>
                      <SelectItem value="success" className="text-white">Success</SelectItem>
                      <SelectItem value="warning" className="text-white">Warning</SelectItem>
                      <SelectItem value="critical" className="text-white">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm text-gray-300">Expiration Date</Label>
                  <div className="mt-1">
                    <Popover open={editDatePopoverOpen} onOpenChange={setEditDatePopoverOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal bg-[#1F1F2E] border-purple-500/30 text-white",
                            !formData.expiresAt && "text-gray-400"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formatDateForButton(formData.expiresAt)}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#1F1F2E] border-purple-500/30">
                        <SimpleCalendar
                          value={formData.expiresAt}
                          onChange={(date) => {
                            console.log('SimpleCalendar (Edit Dialog) onChange triggered with date:', date);
                            handleChange('expiresAt', date);
                            setEditDatePopoverOpen(false);
                          }}
                          minDate={new Date()}
                          className="rounded-md border-purple-500/30"
                        />
                      </PopoverContent>
                    </Popover>
                    {/* Keep Clear Date button */}
                    {formData.expiresAt && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleChange('expiresAt', null)}
                        className="text-xs text-gray-400 mt-2"
                      >
                        Clear Date
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-global"
                  checked={formData.global}
                  onCheckedChange={(checked) => handleChange('global', checked)}
                />
                <Label htmlFor="edit-global" className="text-sm text-gray-300">Show to all users</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-active"
                  checked={formData.isActive}
                  onCheckedChange={(checked) => handleChange('isActive', checked)}
                />
                <Label htmlFor="edit-active" className="text-sm text-gray-300">Active</Label>
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowEditDialog(false)}
                className="bg-transparent border-gray-600 text-gray-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-[#2A2A3E] text-white border-red-500/20">
          <DialogHeader>
            <DialogTitle className="text-xl">Delete Notification</DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <p className="text-gray-300">
              Are you sure you want to delete this notification?
            </p>

            {selectedNotification && (
              <div className="mt-4 p-3 bg-[#1F1F2E] rounded-md border border-red-500/20">
                <div className="flex items-center mb-2">
                  {getLevelIcon(selectedNotification.level)}
                  <h4 className="text-white font-medium ml-2">{selectedNotification.title}</h4>
                </div>
                <p className="text-sm text-gray-300">{selectedNotification.message}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              className="bg-transparent border-gray-600 text-gray-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="small" className="mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete Notification'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationManager;
